/**
 * How a date's age is presented: the color it takes, and the words for it.
 *
 * A date just past reads green and fades through yellow to red as it gets older, so a
 * column of dates shows at a glance which venues were contacted recently and which have
 * gone quiet. Stops are Tailwind's 400 shades — the same ones the rest of the UI uses for
 * colored text — so the ramp sits comfortably on every theme.
 *
 * Callers leave the default text color in place when there is no date ("Never"), rather
 * than picking a color off the ramp for it.
 *
 * `recencyLabel` spells the same age out for a tooltip, since the color alone says "old"
 * but not how old.
 */
import { addMonths, addYears, differenceInCalendarDays, differenceInMonths, differenceInYears } from 'date-fns';

type Stop = { days: number; label: string; rgb: [number, number, number] };

/** Days since the date -> color. Interpolated between stops, clamped outside them. */
const RAMP: Stop[] = [
  { days: 0, label: 'Today', rgb: [74, 222, 128] }, // green-400
  { days: 30, label: '1 month', rgb: [74, 222, 128] }, // green-400
  { days: 31, label: '1 month and 1 day', rgb: [163, 230, 53] }, // lime-400
  { days: 90, label: '3 months', rgb: [250, 204, 21] }, // yellow-400
  { days: 180, label: '6 months', rgb: [251, 146, 60] }, // orange-400
  { days: 365, label: '1 year+', rgb: [248, 113, 113] }, // red-400
  { days: 365*2, label: '2 year+', rgb: [140, 45, 45] }, // red
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function css(rgb: [number, number, number]): string {
  return `rgb(${rgb[0]} ${rgb[1]} ${rgb[2]})`;
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function sample(days: number): [number, number, number] {
  const first = RAMP[0];
  const last = RAMP[RAMP.length - 1];
  // Future dates (a scheduled send) and anything older than the last stop clamp to an end.
  if (days <= first.days) return first.rgb;
  if (days >= last.days) return last.rgb;

  for (let i = 1; i < RAMP.length; i++) {
    const to = RAMP[i];
    if (days > to.days) continue;
    const from = RAMP[i - 1];
    const t = (days - from.days) / (to.days - from.days);
    return [
      lerp(from.rgb[0], to.rgb[0], t),
      lerp(from.rgb[1], to.rgb[1], t),
      lerp(from.rgb[2], to.rgb[2], t),
    ];
  }
  return last.rgb;
}

/**
 * The ramp color for a date, or `undefined` when there is nothing to date — a missing or
 * unparseable value — which leaves the element on the default text color.
 */
export function recencyColor(value: string | Date | null | undefined, now: Date = new Date()): string | undefined {
  if (!value) return undefined;
  const then = value instanceof Date ? value : new Date(value);
  const time = then.getTime();
  if (Number.isNaN(time)) return undefined;

  return css(sample((now.getTime() - time) / MS_PER_DAY));
}

/** The stops, in order, so a legend can be drawn from the same source as the ramp itself. */
export const RECENCY_LEGEND: { label: string; color: string }[] = RAMP.map((stop) => ({
  label: stop.label,
  color: css(stop.rgb),
}));

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/**
 * The gap between two dates, coarsening as it widens: days on their own under a month,
 * months and days up to a quarter, then whole months, a year and its months, and finally
 * whole years. Nobody needs "2 years, 3 months and 4 days" — by then the year is the point.
 */
function gap(from: Date, to: Date): string {
  const days = differenceInCalendarDays(to, from);
  const months = differenceInMonths(to, from);
  // Under a month, and short months: 30 days from Jan 1 has not yet turned a month over.
  if (months < 1) return plural(days, 'day');

  if (months < 3) {
    const trailingDays = differenceInCalendarDays(to, addMonths(from, months));
    return trailingDays === 0
      ? plural(months, 'month')
      : `${plural(months, 'month')} and ${plural(trailingDays, 'day')}`;
  }
  if (months < 12) return plural(months, 'month');

  const years = differenceInYears(to, from);
  if (years === 1) {
    const trailingMonths = differenceInMonths(to, addYears(from, 1));
    return trailingMonths === 0 ? '1 year' : `1 year and ${plural(trailingMonths, 'month')}`;
  }
  return plural(years, 'year');
}

/**
 * The age of a date in words — "12 days ago", "1 month and 3 days ago", "5 months ago",
 * "1 year and 2 months ago", "3 years ago" — for the tooltip on a colored date.
 *
 * Returns `undefined` for a missing or unparseable date, so the caller can drop the
 * tooltip rather than show an empty one.
 */
export function recencyLabel(value: string | Date | null | undefined, now: Date = new Date()): string | undefined {
  if (!value) return undefined;
  const then = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(then.getTime())) return undefined;

  const days = differenceInCalendarDays(now, then);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days === -1) return 'Tomorrow';
  // A date ahead of now — a send still scheduled — reads forwards instead of backwards.
  return days > 0 ? `${gap(then, now)} ago` : `In ${gap(now, then)}`;
}
